import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Award, Download, Building2, ShieldCheck } from "lucide-react";
import axios from "axios";
import Logo from "@/components/Logo";

/** Public verification page — no auth required. */
export default function VerifyCertificatePage() {
  const { certId } = useParams();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [cert, setCert] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${BACKEND}/api/certificates/${certId}`)
      .then((r) => setCert(r.data))
      .catch(() => setError("This certificate could not be found or is no longer valid."));
  }, [BACKEND, certId]);

  return (
    <div className="min-h-screen bg-background">
      <header className="glass-header">
        <div className="max-w-4xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="overline">Certificate Verification</div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 lg:px-10 py-16">
        {error ? (
          <div className="border border-destructive/40 bg-destructive/5 p-10 text-center" data-testid="verify-not-found">
            <div className="font-display font-black text-2xl tracking-tight mb-2 text-destructive">Invalid certificate</div>
            <p className="text-[14px] text-muted-foreground">{error}</p>
            <Link to="/" className="mt-6 h-10 px-5 inline-flex items-center bg-primary text-primary-foreground font-semibold text-[13px]">Back to home</Link>
          </div>
        ) : !cert ? (
          <div className="overline">Verifying...</div>
        ) : (
          <div data-testid="verify-certificate">
            <div className="inline-flex items-center gap-2 text-primary mb-6">
              <ShieldCheck className="h-5 w-5" />
              <span className="overline text-primary">Verified authentic</span>
            </div>
            <h1 className="font-display font-black text-4xl lg:text-6xl tracking-tight leading-[0.95] mb-3">
              {cert.user_name}
            </h1>
            <p className="text-[16px] lg:text-[18px] text-muted-foreground max-w-2xl mb-10">
              has successfully completed the <strong className="text-foreground font-semibold">{cert.module_name}</strong> training program on <strong className="text-foreground font-semibold">Steadfast Learning Hub</strong>.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-0 border border-border bg-white mb-8">
              <div className="p-5 sm:border-r border-border">
                <div className="overline mb-2">Certificate ID</div>
                <div className="font-mono font-semibold text-[13px] break-all">{cert.cert_id}</div>
              </div>
              <div className="p-5 sm:border-r border-border border-t sm:border-t-0">
                <div className="overline mb-2">Issued on</div>
                <div className="font-display font-bold text-[15px]">{cert.issued_at?.slice(0, 10)}</div>
              </div>
              <div className="p-5 border-t sm:border-t-0">
                <div className="overline mb-2">Scope</div>
                <div className="font-display font-bold text-[15px]">
                  {cert.total_sections ? `${cert.total_sections} sections` : ""}
                  {cert.total_sections && cert.total_topics ? " · " : ""}
                  {cert.total_topics ? `${cert.total_topics} topics` : ""}
                  {!cert.total_sections && !cert.total_topics ? "Full program" : ""}
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <a data-testid="verify-download-button" href={`${BACKEND}/api/certificates/${cert.cert_id}/download`} target="_blank" rel="noreferrer"
                 className="h-11 px-5 inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold text-[13px] hover:bg-[#001d6b]">
                <Download className="h-4 w-4" /> Download official PDF
              </a>
              <Link to="/" className="h-11 px-5 inline-flex items-center gap-2 border border-border font-semibold text-[13px] hover:bg-accent">
                <Building2 className="h-4 w-4" /> Visit Steadfast Learning Hub
              </Link>
            </div>

            <div className="mt-12 border-t border-border pt-6 flex items-start gap-3 text-[12.5px] text-muted-foreground">
              <Award className="h-4 w-4 shrink-0 mt-0.5 text-primary" />
              <p>
                Need to confirm the authenticity of this credential? Contact <a href="mailto:operations@steadfastitconsulting.com" className="text-primary link-underline">operations@steadfastitconsulting.com</a> with the certificate ID above.
              </p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
